import { CreateNotificationSubscriptionT, NotificationPreferencesT } from '../types/notificationSubscriptionT'

/**
 * Utility functions for managing web push notifications
 */
class WebPushService {
  private static vapidPublicKey: string | null = null

  /**
   * Set the VAPID public key
   */
  static setVapidKey(key: string) {
    this.vapidPublicKey = key
  }

  /**
   * Initialize service worker and message listener (without subscribing to push)
   */
  static async initialize(): Promise<void> {
    if (!this.isSupported()) {
      return
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) {
        await navigator.serviceWorker.register('/sw.js')
      }

      this.setupMessageListener()
    } catch (error) {
      // Service worker initialization failed silently
    }
  }

  /**
   * Check if the browser supports service workers and push notifications
   */
  static isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  /**
   * Check if notifications are currently enabled
   */
  static isNotificationEnabled(): boolean {
    return Notification.permission === 'granted'
  }

  /**
   * Request notification permission from the user
   */
  static async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Web push notifications are not supported in this browser')
    }

    const permission = await Notification.requestPermission()
    return permission
  }

  /**
   * Register service worker and get push subscription
   */
  static async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      throw new Error('Web push notifications are not supported in this browser')
    }

    if (!this.vapidPublicKey) {
      throw new Error('VAPID public key not set. Please refresh the page and try again.')
    }

    // Request permission if not already granted
    const permission = await this.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted. Please allow notifications in your browser.')
    }

    try {
      // Register service worker with timeout
      const registrationPromise = navigator.serviceWorker.register('/sw.js')
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Service worker registration timed out. This may be due to network issues or browser restrictions.')), 10000)
      )

      const registration = await Promise.race([registrationPromise, timeoutPromise])

      this.setupMessageListener()

      // Wait for service worker to be ready with timeout
      const readyPromise = navigator.serviceWorker.ready
      const readyTimeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Service worker activation timed out. Please refresh the page and try again.')), 10000)
      )

      await Promise.race([readyPromise, readyTimeoutPromise])

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription()
      if (existingSubscription) {
        return existingSubscription
      }

      // Create new subscription with timeout
      const subscriptionPromise = registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey),
      })
      const subscriptionTimeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Push subscription creation timed out. This may be due to browser restrictions on localhost or development environments.')), 10000)
      )

      const subscription = await Promise.race([subscriptionPromise, subscriptionTimeoutPromise])

      return subscription
    } catch (error) {
      // Push subscription failed - provide specific error messages below

      // Provide more specific error messages for common issues
      if (error instanceof Error) {
        if (error.message.includes('registration')) {
          throw new Error('Failed to register service worker. Please check your internet connection and try again.')
        } else if (error.message.includes('subscription')) {
          throw new Error('Failed to create push subscription. This is common on localhost - web push notifications require HTTPS in production.')
        } else if (error.message.includes('timeout')) {
          throw error // Use the timeout error message as-is
        }
      }

      throw error
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  static async unsubscribeFromPush(): Promise<void> {
    if (!this.isSupported()) {
      return
    }

    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
      }
    }
  }

  /**
   * Convert a base64 VAPID key to Uint8Array
   */
  private static urlBase64ToUint8Array(base64String: string): BufferSource {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  /**
   * Convert a PushSubscription to the format expected by the API
   */
  static subscriptionToApiFormat(
    subscription: PushSubscription,
    preferences: NotificationPreferencesT
  ): CreateNotificationSubscriptionT {
    const keys = subscription.toJSON().keys
    if (!keys || !keys.p256dh || !keys.auth) {
      throw new Error('Invalid subscription keys')
    }

    return {
      platform: 'web',
      endpoint: subscription.endpoint,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
      preferences,
    }
  }

  /**
   * Show a test notification
   */
  static async showTestNotification(): Promise<void> {
    if (this.isNotificationEnabled()) {
      // eslint-disable-next-line no-new
      new Notification('Test Notification', {
        body: 'This is a test notification from Good To Book',
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'test-notification',
      })
    }
  }

  /**
   * Set up message listener for service worker messages
   */
  static setupMessageListener(): void {
    if (!this.isSupported()) {
      return
    }

    navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage)

    navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage)
  }

  /**
   * Handle messages from the service worker
   */
  private static handleServiceWorkerMessage = (event: MessageEvent) => {
    if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
      const { url } = event.data

      if (url && url !== window.location.pathname) {
        window.location.href = url
      }
    }
  }
}

export default WebPushService
